import { useEffect } from "react"
import { useCreateAddress } from "../../hooks/People/useCreateAddress"
import { AddressEntity } from "../../Objects/Person/AddressEntity"
import { useTranslation } from "react-i18next"
import ModalPortal from "../../components/Modal/ModalPortal"
import { DeliveryMapPicker } from "../../components/DeliveryMapPicker/DeliveryMapPicker"
import { AddressPreviewCard } from "../../components/Address/AddressPreviewCardProps"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import InputField from "../../components/InputField/InputField"
import { faCircleInfo, faTag } from "@fortawesome/free-solid-svg-icons"
import Button from "../../components/Button/Button"
import { THEME } from "../../configuration/Theme"

interface ProfileAddressPupop {
  value: AddressEntity[]
  addressEntity?: AddressEntity
  onClose: () => void
  onChange: (addresses: AddressEntity[]) => void
}

export const ProfileAddressPupop: React.FC<ProfileAddressPupop> = ({ value, addressEntity, onClose, onChange }) => {
  const { t } = useTranslation("address")

  const {
    label,
    city,
    street,
    neighBornHood,
    number,
    complement,
    cep,
    latitude,
    longitude,
    state,
    handleSubmitForm,
    handleSetInitialValue,
    handleLabelChange,
    handleComplementChange,
    handleApplyGoogleAddress,
  } = useCreateAddress()

  useEffect(() => {
    handleSetInitialValue(addressEntity)
  }, [addressEntity])

  const handleSave = () => {
    const address = handleSubmitForm()

    if (!address) {
      return
    }

    const list = [...value]

    const index = list.findIndex((item) => item.id === address.id)

    if (index >= 0) {
      list[index] = address
    } else {
      list.push(address)
    }

    onChange(list)

    onClose()
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className="p-4">
            <div className="bg-white w-full max-w-xl ring-2 ring-orange-500 rounded-md p-4 shadow-2xl animate-in zoom-in-95 duration-500 overflow-y-auto max-h-[92vh] no-scrollbar">
              <div className="flex justify-between items-center mb-8">
                <div>
                  <h3 className="text-xl font-black text-gray-900 uppercase tracking-tight">
                    {addressEntity ? t("publicMenu:address_modal.edit_title") : t("publicMenu:address_modal.new_title")}
                  </h3>

                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />

                    <span className="text-[9px] font-black text-gray-400 uppercase tracking-[0.2em]">
                      Identificador: {addressEntity?.id}
                    </span>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full text-gray-400 hover:text-gray-900 transition-colors"
                  data-interaction-description-key="interactionDescriptions:publicMenu.pupop.profileAddressPupop.button.onClose"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={handleSave} className="grid grid-cols-12 gap-x-3 gap-y-4">
                <div className="col-span-12">
                  <div className="mb-2">
                    <InputField
                      required
                      maxLength={45}
                      type="text"
                      title={t("publicMenu:address_modal.identification_placeholder")}
                      placeholder={t("label_person_label_place_holder")}
                      onInvalidMessage={t("label_person_label_onInvalidMessage")}
                      icon={faTag}
                      value={label}
                      onChange={handleLabelChange}
                      descriptionKey="interactionDescriptions:publicMenu.pupop.profileAddressPupop.input.personPlaceHolder"
                    />
                  </div>

                  <div className="mb-4">
                    <InputField
                      maxLength={120}
                      type="text"
                      placeholder={t("label_location_details_place_holder")}
                      onInvalidMessage={t("label_location_details_onInvalidMessage")}
                      icon={faCircleInfo}
                      value={complement}
                      onChange={handleComplementChange}
                      descriptionKey="interactionDescriptions:publicMenu.pupop.profileAddressPupop.input.locationDetailsPlaceHolder"
                    />
                  </div>

                  <DeliveryMapPicker
                    initialLatitude={addressEntity?.latitude}
                    initialLongitude={addressEntity?.longitude}
                    onSelect={({ addressComponents, latitude, longitude }) => {
                      if (!addressComponents) {
                        return
                      }

                      handleApplyGoogleAddress({
                        addressComponents,
                        latitude,
                        longitude,
                      })
                    }}
                  />

                  <AddressPreviewCard
                    street={street}
                    number={number}
                    neighborhood={neighBornHood}
                    city={city}
                    state={state}
                    cep={cep}
                    complement={complement}
                    latitude={latitude}
                    longitude={longitude}
                  />
                </div>

                <div className="col-span-12 pt-8">
                  <Button
                    typeButton="submit"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    label={
                      addressEntity
                        ? t("publicMenu:address_modal.save_changes")
                        : t("publicMenu:address_modal.register_address")
                    }
                    descriptionKey="interactionDescriptions:publicMenu.pupop.profileAddressPupop.button.saveChanges"
                  />
                </div>
              </form>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
